<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { Chart, registerables } from "chart.js";

// Register Chart.js components
Chart.register(...registerables);

// Refs untuk chart
const semesterChartRef = ref(null);
const semesterChartInstance = ref(null);
const yearlyChartRef = ref(null);
const yearlyChartInstance = ref(null);

// Props
const props = defineProps({
  grafik: {
    type: Object,
    required: true,
  },
});

// Default values for filters
const selectedSemester = ref("");
const selectedTahunAkademik = ref("semua"); // Default: tampilkan semua tahun

// Computed untuk mendapatkan daftar tahun akademik unik
const availableTahunAkademik = computed(() => {
  if (!chartData.value || chartData.value.length === 0) return [];

  // Ambil semua tahun akademik dari data
  const tahunList = chartData.value
    .map((item) => item.tahun_akademik)
    .filter(Boolean);

  // Hapus duplikat dan urutkan
  const uniqueTahunList = [...new Set(tahunList)].sort();

  // Tambahkan opsi "semua tahun" di awal array
  return ["semua", ...uniqueTahunList];
});

// Process chart data from props
const chartData = computed(() => {
  // Check if props.grafik is an array (GrafikResource collection) or has data property
  if (Array.isArray(props.grafik)) {
    return props.grafik;
  }
  return props.grafik && props.grafik.data ? props.grafik.data : [];
});

// Compute processed chart data for visualization
const processedChartData = computed(() => {
  if (!chartData.value || chartData.value.length === 0) return [];

  // Filter data berdasarkan tahun akademik yang dipilih
  let filteredData = chartData.value;
  if (selectedTahunAkademik.value !== "semua") {
    filteredData = chartData.value.filter(
      (item) => item.tahun_akademik === selectedTahunAkademik.value
    );

    // Jika tidak ada data setelah filter, kembalikan array kosong
    if (filteredData.length === 0) return [];
  }

  // First, fetch all unique prodi codes to ensure we include all program studies
  const uniqueProdiCodes = [
    ...new Set(filteredData.map((item) => item.kode_prodi)),
  ];

  // Create results array
  const results = [];

  try {
    // For each unique prodi code
    uniqueProdiCodes.forEach((kode_prodi) => {
      // Filter items for this prodi
      const prodiItems = filteredData.filter(
        (item) => item.kode_prodi === kode_prodi
      );

      if (prodiItems.length > 0) {
        // Get the study program info from the first item
        const studyProgram =
          prodiItems[0].studyProgram || prodiItems[0].study_program;

        // Initialize counters
        let ganjilCount = 0;
        let genapCount = 0;

        // Process each item for this prodi
        prodiItems.forEach((item) => {
          // Get the semester value and convert to a number for safety
          const mahasiswaCount = parseInt(
            item.jumlah_mahasiswa || item.total_mahasiswa || 0
          );

          // Add to the appropriate counter based on semester
          if (item.semester === "ganjil") {
            ganjilCount += mahasiswaCount;
          } else if (item.semester === "genap") {
            genapCount += mahasiswaCount;
          } else {
            // If semester is not specified but can be inferred from tahun_semester
            if (item.tahun_semester) {
              if (item.tahun_semester.toLowerCase().includes("ganjil")) {
                ganjilCount += mahasiswaCount;
              } else if (item.tahun_semester.toLowerCase().includes("genap")) {
                genapCount += mahasiswaCount;
              }
            }
          }
        });

        // Calculate total after processing all items for this prodi
        const totalCount = ganjilCount + genapCount;

        // Get the most recent tahun_akademik for this prodi
        const latestTahunAkademik =
          prodiItems
            .filter((item) => item.tahun_akademik)
            .sort((a, b) => {
              // Sort by tahun_akademik (assuming format is like "2022/2023")
              return b.tahun_akademik.localeCompare(a.tahun_akademik);
            })[0]?.tahun_akademik || "N/A";

        // Add to results
        results.push({
          kode_prodi,
          study_program: studyProgram,
          // Primary fields used in tables and displays
          tahun_akademik: latestTahunAkademik,
          jumlah_mahasiswa_ganjil: ganjilCount,
          jumlah_mahasiswa_genap: genapCount,
          // These fields are for backward compatibility
          ganjil: ganjilCount,
          genap: genapCount,
          total_mahasiswa: totalCount,
        });
      }
    });

    // Sort results by total_mahasiswa in descending order
    results.sort((a, b) => b.total_mahasiswa - a.total_mahasiswa);

    return results;
  } catch (error) {
    console.error("Error processing chart data:", error);
    return [];
  }
});

// Compute totals and percentages
const totalMahasiswa = computed(() => {
  // Get the total students across all programs
  if (!processedChartData.value || !Array.isArray(processedChartData.value))
    return 0;

  return processedChartData.value.reduce(
    (total, item) => total + (item.total_mahasiswa || 0),
    0
  );
});

const totalGanjil = computed(() => {
  if (!processedChartData.value || !Array.isArray(processedChartData.value))
    return 0;
  return processedChartData.value.reduce(
    (total, item) => total + (item.jumlah_mahasiswa_ganjil || 0),
    0
  );
});

const totalGenap = computed(() => {
  if (!processedChartData.value || !Array.isArray(processedChartData.value))
    return 0;
  return processedChartData.value.reduce(
    (total, item) => total + (item.jumlah_mahasiswa_genap || 0),
    0
  );
});

const totalProdi = computed(() => {
  return processedChartData.value.length;
});

const averagePerProdi = computed(() => {
  if (totalProdi.value === 0) return 0;
  return Math.round(totalMahasiswa.value / totalProdi.value);
});

const ganjilPercent = computed(() => {
  if (totalMahasiswa.value === 0) return 0;
  return ((totalGanjil.value / totalMahasiswa.value) * 100).toFixed(1);
});

const genapPercent = computed(() => {
  if (totalMahasiswa.value === 0) return 0;
  return ((totalGenap.value / totalMahasiswa.value) * 100).toFixed(1);
});

// Computed property untuk cek ketersediaan data
const hasData = computed(() => {
  return processedChartData.value && processedChartData.value.length > 0;
});

// Computed untuk data perbandingan per tahun akademik
const yearlyComparisonData = computed(() => {
  if (!chartData.value || chartData.value.length === 0) return [];

  // Group data by academic year
  const yearGroups = {};

  // First pass: collect all data per year
  chartData.value.forEach((item) => {
    if (!item.tahun_akademik) return;

    if (!yearGroups[item.tahun_akademik]) {
      yearGroups[item.tahun_akademik] = {
        tahun_akademik: item.tahun_akademik,
        ganjil: 0,
        genap: 0,
        total: 0,
      };
    }

    const count = parseInt(item.jumlah_mahasiswa || item.total_mahasiswa || 0);
    if (item.semester === "ganjil") {
      yearGroups[item.tahun_akademik].ganjil += count;
    } else if (item.semester === "genap") {
      yearGroups[item.tahun_akademik].genap += count;
    } else if (item.tahun_semester) {
      if (item.tahun_semester.toLowerCase().includes("ganjil")) {
        yearGroups[item.tahun_akademik].ganjil += count;
      } else if (item.tahun_semester.toLowerCase().includes("genap")) {
        yearGroups[item.tahun_akademik].genap += count;
      }
    }

    yearGroups[item.tahun_akademik].total += count;
  });

  // Convert to array and sort by year
  const result = Object.values(yearGroups).sort((a, b) =>
    a.tahun_akademik.localeCompare(b.tahun_akademik)
  );

  // Second pass: calculate growth percentages
  if (result.length > 1) {
    for (let i = 1; i < result.length; i++) {
      const currentYear = result[i];
      const previousYear = result[i - 1];

      const growth = currentYear.total - previousYear.total;
      const growthPercent =
        previousYear.total > 0
          ? ((growth / previousYear.total) * 100).toFixed(1)
          : 0;

      result[i].growth = growth;
      result[i].growthPercent = growthPercent;
    }
  }

  return result;
});

// Computed property untuk mendapatkan tahun akademik terbaru
const latestAcademicYear = computed(() => {
  if (!processedChartData.value || processedChartData.value.length === 0) {
    return "N/A";
  }

  // Ambil tahun akademik dari data pertama (sudah diurutkan dari terbaru)
  const tahunAkademikList = processedChartData.value
    .map((item) => item.tahun_akademik)
    .filter(Boolean);

  if (tahunAkademikList.length === 0) {
    return "N/A";
  }

  // Hapus duplikat tahun akademik
  const uniqueTahunList = [...new Set(tahunAkademikList)].sort();

  // Jika hanya ada satu tahun akademik, tampilkan itu saja
  if (uniqueTahunList.length === 1) {
    return uniqueTahunList[0];
  }

  // Jika ada beberapa tahun akademik, tampilkan rentang dari yang terlama ke yang terbaru
  return `${uniqueTahunList[0]} - ${
    uniqueTahunList[uniqueTahunList.length - 1]
  }`;
});

// Computed property untuk menghitung jumlah tahun akademik unik
const academicYearCount = computed(() => {
  if (!processedChartData.value || processedChartData.value.length === 0) {
    return 0;
  }

  const tahunAkademikList = processedChartData.value
    .map((item) => item.tahun_akademik)
    .filter(Boolean);

  // Hapus duplikat tahun akademik
  const uniqueTahunList = [...new Set(tahunAkademikList)];

  return uniqueTahunList.length;
});

// Fungsi untuk membuat chart semester
const createSemesterChart = (yearLabel = "") => {
  if (semesterChartRef.value) {
    // Pastikan untuk menghancurkan instance chart sebelumnya jika ada
    if (semesterChartInstance.value) {
      semesterChartInstance.value.destroy();
    }

    // Data untuk chart
    const labels = ["Semester Ganjil", "Semester Genap"];
    const data = [totalGanjil.value, totalGenap.value];
    const backgroundColor = [
      "rgba(59, 130, 246, 0.7)",
      "rgba(16, 185, 129, 0.7)",
    ];
    const borderColor = ["rgb(37, 99, 235)", "rgb(5, 150, 105)"];

    // Buat chart baru
    semesterChartInstance.value = new Chart(semesterChartRef.value, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Jumlah Mahasiswa",
            data: data,
            backgroundColor: backgroundColor,
            borderColor: borderColor,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
              callback: function (value) {
                return value.toLocaleString();
              },
            },
            title: {
              display: true,
              text: "Jumlah Mahasiswa",
            },
          },
          x: {
            title: {
              display: true,
              text: `Semester ${yearLabel}`,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const value = context.raw;
                const total = totalGanjil.value + totalGenap.value;
                const percentage =
                  total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${value.toLocaleString()} mahasiswa (${percentage}%)`;
              },
            },
          },
          datalabels: {
            anchor: "end",
            align: "top",
            formatter: function (value) {
              return value.toLocaleString();
            },
            color: "black",
            font: {
              weight: "bold",
            },
          },
        },
      },
    });
  }
};

// Fungsi untuk membuat chart perbandingan tahun akademik
const createYearlyComparisonChart = () => {
  if (yearlyChartRef.value) {
    // Pastikan untuk menghancurkan instance chart sebelumnya jika ada
    if (yearlyChartInstance.value) {
      yearlyChartInstance.value.destroy();
    }

    // Skip if no data
    if (yearlyComparisonData.value.length <= 1) return;

    // Data untuk chart
    const labels = yearlyComparisonData.value.map(
      (item) => item.tahun_akademik
    );
    const totalData = yearlyComparisonData.value.map((item) => item.total);
    const ganjilData = yearlyComparisonData.value.map((item) => item.ganjil);
    const genapData = yearlyComparisonData.value.map((item) => item.genap);

    // Buat chart baru
    yearlyChartInstance.value = new Chart(yearlyChartRef.value, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Total",
            data: totalData,
            backgroundColor: "rgba(99, 102, 241, 0.8)",
            borderColor: "rgb(79, 70, 229)",
            borderWidth: 1,
            type: "line",
            fill: false,
            tension: 0.1,
            order: 0,
          },
          {
            label: "Semester Ganjil",
            data: ganjilData,
            backgroundColor: "rgba(59, 130, 246, 0.7)",
            borderColor: "rgb(37, 99, 235)",
            borderWidth: 1,
            order: 1,
          },
          {
            label: "Semester Genap",
            data: genapData,
            backgroundColor: "rgba(16, 185, 129, 0.7)",
            borderColor: "rgb(5, 150, 105)",
            borderWidth: 1,
            order: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
              callback: function (value) {
                return value.toLocaleString();
              },
            },
            title: {
              display: true,
              text: "Jumlah Mahasiswa",
            },
          },
          x: {
            title: {
              display: true,
              text: "Tahun Akademik",
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const value = context.raw;
                return `${
                  context.dataset.label
                }: ${value.toLocaleString()} mahasiswa`;
              },
              afterBody: function (tooltipItems) {
                const index = tooltipItems[0].dataIndex;
                if (index === 0) return []; // Skip for first year

                const item = yearlyComparisonData.value[index];
                if (item.growth === undefined) return [];

                const growthText = `Pertumbuhan: ${
                  item.growth > 0 ? "+" : ""
                }${item.growth.toLocaleString()} (${item.growthPercent}%)`;
                return [growthText];
              },
            },
          },
        },
      },
    });
  }
};

// Update chart ketika data berubah
watch(
  [
    totalGanjil,
    totalGenap,
    processedChartData,
    latestAcademicYear,
    selectedTahunAkademik,
    yearlyComparisonData,
  ],
  () => {
    if (processedChartData.value && processedChartData.value.length > 0) {
      // Get the latest academic year for chart title
      const yearLabel =
        selectedTahunAkademik.value !== "semua"
          ? `(${selectedTahunAkademik.value})`
          : latestAcademicYear.value !== "N/A"
          ? `(${latestAcademicYear.value})`
          : "";
      createSemesterChart(yearLabel);
    }

    // Create or update yearly comparison chart if we have data
    if (yearlyComparisonData.value && yearlyComparisonData.value.length > 1) {
      createYearlyComparisonChart();
    }
  },
  { deep: true }
);

// Inisialisasi chart setelah komponen dimount
onMounted(() => {
  // Sedikit delay untuk memastikan DOM sudah siap
  setTimeout(() => {
    if (processedChartData.value && processedChartData.value.length > 0) {
      const yearLabel =
        latestAcademicYear.value !== "N/A"
          ? `(${latestAcademicYear.value})`
          : "";
      createSemesterChart(yearLabel);
    }

    // Initialize yearly comparison chart if we have data for multiple years
    if (yearlyComparisonData.value && yearlyComparisonData.value.length > 1) {
      createYearlyComparisonChart();
    }
  }, 300);
});
</script>
<style scoped></style>

<template>
  <div>
    <section id="home" class="py-16 lg:py-20">
      <!-- Hero Content Start -->
      <div class="mx-auto max-w-[1170px] px-4 sm:px-8 xl:px-0 relative z-1">
        <div class="flex flex-wrap gap-x-7.5 gap-y-9">
          <!-- Hero item -->
          <div
            class="max-w-[1170px] w-full flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8 bg-white rounded-xl p-4 lg:p-2.5"
          >
            <!-- Data Table -->
            <div class="w-full p-4 mb-4 bg-white rounded-lg sm:p-6 sm:mb-6">
              <div
                class="flex flex-col items-start justify-between gap-3 mb-3 sm:flex-row sm:items-center"
              >
                <h4 class="text-base font-medium text-gray-800">
                  Ringkasan Distribusi Mahasiswa
                </h4>

                <div class="flex flex-wrap items-center gap-2">
                  <!-- Filter Tahun Akademik -->
                  <div
                    v-if="availableTahunAkademik.length > 1"
                    class="flex items-center"
                  >
                    <label for="filterTahun" class="mr-2 text-xs text-gray-600"
                      >Tahun Akademik:</label
                    >
                    <select
                      id="filterTahun"
                      v-model="selectedTahunAkademik"
                      class="py-1 pl-2 pr-8 text-xs text-gray-700 bg-white border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                    >
                      <option value="semua">Semua Tahun</option>
                      <option
                        v-for="tahun in availableTahunAkademik.filter(
                          (t) => t !== 'semua'
                        )"
                        :key="tahun"
                        :value="tahun"
                      >
                        {{ tahun }}
                      </option>
                    </select>
                  </div>

                  <div
                    class="flex items-center px-3 py-1 rounded-full bg-blue-50"
                  >
                    <span class="mr-1 text-xs text-gray-500">
                      {{
                        academicYearCount > 1
                          ? "Tahun Akademik:"
                          : "Tahun Akademik:"
                      }}
                    </span>
                    <span class="text-sm font-medium text-blue-700">{{
                      latestAcademicYear
                    }}</span>
                    <span
                      v-if="academicYearCount > 1"
                      class="ml-1 text-xs text-gray-500"
                      >({{ academicYearCount }} tahun)</span
                    >
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div class="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium text-gray-700"
                      >Distribusi per Semester</span
                    >
                    <span class="text-xs font-medium text-gray-500"
                      >Total:
                      {{ totalMahasiswa.toLocaleString() }} mahasiswa</span
                    >
                  </div>

                  <!-- Tampilkan progress bar jika data tersedia -->
                  <div v-if="hasData" class="flex items-center pt-4 mb-4">
                    <div
                      class="relative w-full overflow-hidden bg-gray-100 rounded-full h-7"
                    >
                      <div
                        class="absolute top-0 left-0 h-full bg-blue-500 rounded-l-full"
                        :style="{ width: `${ganjilPercent}%` }"
                      ></div>
                      <div
                        class="absolute top-0 right-0 h-full bg-green-500 rounded-r-full"
                        :style="{ width: `${genapPercent}%` }"
                      ></div>

                      <!-- Percentage labels on the bars -->
                      <div
                        class="absolute inset-0 flex items-center justify-between px-3"
                      >
                        <span
                          v-if="ganjilPercent > 15"
                          class="z-10 text-xs font-medium text-white"
                          >{{ ganjilPercent }}%</span
                        >
                        <span
                          v-if="genapPercent > 15"
                          class="z-10 text-xs font-medium text-white"
                          >{{ genapPercent }}%</span
                        >
                      </div>

                      <!-- Indikator tahun akademik yang dipilih -->
                      <div
                        v-if="selectedTahunAkademik !== 'semua'"
                        class="absolute top-0 left-1/2 transform -translate-x-1/2 px-2 py-0.5 bg-white text-xs text-blue-600 rounded-full shadow-sm border border-blue-200"
                        style="margin-top: -14px; z-index: 20"
                      >
                        {{ selectedTahunAkademik }}
                      </div>
                    </div>
                  </div>

                  <!-- Tampilkan pesan jika data tidak tersedia -->
                  <div
                    v-else
                    class="flex items-center justify-center mb-4 bg-gray-100 rounded-full h-7"
                  >
                    <span class="text-xs text-gray-500">Tidak ada data</span>
                  </div>

                  <div class="flex justify-between text-sm">
                    <div class="flex items-center">
                      <span
                        class="inline-block w-3 h-3 mr-2 bg-blue-500 rounded-full"
                      ></span>
                      <div>
                        <span class="font-medium">Semester Ganjil</span>
                        <div class="text-xs text-gray-600">
                          {{ totalGanjil.toLocaleString() }} mahasiswa
                        </div>
                      </div>
                    </div>

                    <div class="flex items-center">
                      <span
                        class="inline-block w-3 h-3 mr-2 bg-green-500 rounded-full"
                      ></span>
                      <div>
                        <span class="font-medium">Semester Genap</span>
                        <div class="text-xs text-gray-600">
                          {{ totalGenap.toLocaleString() }} mahasiswa
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="p-4 border border-gray-100 rounded-lg bg-gray-50">
                  <div class="mb-2">
                    <span class="text-sm font-medium text-gray-700"
                      >Statistik Program Studi</span
                    >
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-xl font-bold text-blue-600">
                        {{ totalProdi }}
                      </div>
                      <div class="text-xs text-gray-500">
                        Total Program Studi
                      </div>
                    </div>

                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-xl font-bold text-green-600">
                        {{ averagePerProdi.toLocaleString() }}
                      </div>
                      <div class="text-xs text-gray-500">
                        Rata-rata Mahasiswa
                      </div>
                    </div>

                    <div
                      v-if="processedChartData && processedChartData.length > 0"
                      class="p-3 bg-white border border-gray-100 rounded-lg"
                    >
                      <div class="text-xl font-bold text-purple-600">
                        {{
                          processedChartData[0].total_mahasiswa.toLocaleString()
                        }}
                      </div>
                      <div class="text-xs text-gray-500">
                        Prodi Terbanyak:
                        <span class="font-medium">
                          {{
                            processedChartData[0].study_program?.name || "N/A"
                          }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-else
                      class="p-3 bg-white border border-gray-100 rounded-lg"
                    >
                      <div class="text-xl font-bold text-purple-600">0</div>
                      <div class="text-xs text-gray-500">Prodi Terbanyak</div>
                    </div>

                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-xl font-bold text-amber-600">
                        {{
                          selectedSemester === "ganjil"
                            ? "Ganjil"
                            : selectedSemester === "genap"
                            ? "Genap"
                            : totalGanjil > totalGenap
                            ? "Ganjil"
                            : "Genap"
                        }}
                      </div>
                      <div class="text-xs text-gray-500">Semester Dominan</div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Semester Chart -->
              <div class="p-4 mt-6 bg-white border border-gray-100 rounded-lg">
                <div class="mb-3">
                  <div class="flex items-center justify-between mb-1">
                    <h5 class="text-sm font-medium text-gray-700">
                      Grafik Distribusi Mahasiswa Berdasarkan Semester
                    </h5>
                    <div v-if="hasData" class="flex items-center space-x-4">
                      <div class="flex items-center">
                        <span
                          class="inline-block w-3 h-3 mr-2 bg-blue-500 rounded"
                        ></span>
                        <span class="text-xs text-gray-600">Ganjil</span>
                      </div>
                      <div class="flex items-center">
                        <span
                          class="inline-block w-3 h-3 mr-2 bg-green-500 rounded"
                        ></span>
                        <span class="text-xs text-gray-600">Genap</span>
                      </div>
                    </div>

                    <div
                      v-if="
                        selectedTahunAkademik === 'semua' &&
                        academicYearCount > 1
                      "
                      class="text-xs text-yellow-600"
                    >
                      <span>Menampilkan data dari semua tahun akademik</span>
                    </div>
                  </div>

                  <div v-if="hasData" class="flex flex-wrap items-center">
                    <span class="text-xs text-gray-500">Tahun Akademik: </span>
                    <span class="ml-1 text-xs font-medium text-gray-700">{{
                      latestAcademicYear
                    }}</span>
                    <span
                      v-if="academicYearCount > 1"
                      class="ml-1 text-xs text-gray-500"
                    >
                      ({{ academicYearCount }} tahun akademik)
                    </span>
                  </div>
                </div>

                <!-- Tampilkan grafik jika data tersedia -->
                <div v-if="hasData" class="h-64">
                  <canvas ref="semesterChartRef"></canvas>
                  <div class="mt-2 text-xs text-center text-gray-500">
                    Total Mahasiswa: {{ totalMahasiswa.toLocaleString() }}
                  </div>
                </div>

                <!-- Tampilkan pesan jika tidak ada data -->
                <div
                  v-else
                  class="flex flex-col items-center justify-center h-64 rounded-lg bg-gray-50"
                >
                  <svg
                    class="w-12 h-12 mb-2 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    ></path>
                  </svg>
                  <p class="text-sm text-gray-500">
                    Tidak ada data grafik yang tersedia
                  </p>
                  <p class="mt-1 text-xs text-gray-400">
                    Silahkan periksa kembali filter atau tambahkan data baru
                  </p>
                </div>
              </div>

              <!-- Yearly Comparison Chart -->
              <div
                v-if="yearlyComparisonData.length > 1"
                class="p-4 mt-6 bg-white border border-gray-100 rounded-lg"
              >
                <div class="mb-3">
                  <div class="flex items-center justify-between mb-1">
                    <h5 class="text-sm font-medium text-gray-700">
                      Perbandingan Jumlah Mahasiswa per Tahun Akademik
                    </h5>
                    <div class="flex items-center gap-3">
                      <div class="flex items-center">
                        <span
                          class="inline-block w-3 h-3 mr-2 bg-indigo-500 rounded"
                        ></span>
                        <span class="text-xs text-gray-600">Total</span>
                      </div>
                      <div class="flex items-center">
                        <span
                          class="inline-block w-3 h-3 mr-2 bg-blue-500 rounded"
                        ></span>
                        <span class="text-xs text-gray-600">Ganjil</span>
                      </div>
                      <div class="flex items-center">
                        <span
                          class="inline-block w-3 h-3 mr-2 bg-green-500 rounded"
                        ></span>
                        <span class="text-xs text-gray-600">Genap</span>
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-wrap items-center">
                    <span class="text-xs text-gray-500">Periode: </span>
                    <span class="ml-1 text-xs font-medium text-gray-700">
                      {{ yearlyComparisonData[0]?.tahun_akademik }} -
                      {{
                        yearlyComparisonData[yearlyComparisonData.length - 1]
                          ?.tahun_akademik
                      }}
                    </span>
                    <span class="ml-2 text-xs text-gray-500">
                      ({{ yearlyComparisonData.length }} tahun akademik)
                    </span>
                  </div>
                </div>

                <!-- Tampilkan grafik perbandingan tahun -->
                <div class="h-64">
                  <canvas ref="yearlyChartRef"></canvas>
                </div>

                <!-- Tabel perbandingan tahun -->
                <div class="mt-4 overflow-x-auto">
                  <table class="w-full text-sm text-left text-gray-700">
                    <thead class="text-xs text-gray-700 uppercase bg-gray-100">
                      <tr>
                        <th scope="col" class="px-4 py-2">Tahun Akademik</th>
                        <th scope="col" class="px-4 py-2 text-right">Ganjil</th>
                        <th scope="col" class="px-4 py-2 text-right">Genap</th>
                        <th scope="col" class="px-4 py-2 text-right">Total</th>
                        <th scope="col" class="px-4 py-2 text-right">
                          Pertumbuhan
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, index) in yearlyComparisonData"
                        :key="index"
                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                      >
                        <td class="px-4 py-2 font-medium">
                          {{ item.tahun_akademik }}
                        </td>
                        <td class="px-4 py-2 text-right">
                          {{ item.ganjil.toLocaleString() }}
                        </td>
                        <td class="px-4 py-2 text-right">
                          {{ item.genap.toLocaleString() }}
                        </td>
                        <td class="px-4 py-2 font-medium text-right">
                          {{ item.total.toLocaleString() }}
                        </td>
                        <td class="px-4 py-2 text-right">
                          <div
                            v-if="index > 0"
                            class="flex items-center justify-end"
                          >
                            <span
                              :class="
                                item.growth > 0
                                  ? 'text-green-600'
                                  : item.growth < 0
                                  ? 'text-red-600'
                                  : 'text-gray-600'
                              "
                              class="font-medium"
                            >
                              {{ item.growth > 0 ? "+" : ""
                              }}{{ item.growth.toLocaleString() }}
                            </span>
                            <span class="ml-1 text-xs text-gray-500">
                              ({{ item.growth > 0 ? "+" : ""
                              }}{{ item.growthPercent }}%)
                            </span>
                            <svg
                              v-if="item.growth > 0"
                              class="w-4 h-4 ml-1 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                              ></path>
                            </svg>
                            <svg
                              v-else-if="item.growth < 0"
                              class="w-4 h-4 ml-1 text-red-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"
                              ></path>
                            </svg>
                            <svg
                              v-else
                              class="w-4 h-4 ml-1 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M18 12H6"
                              ></path>
                            </svg>
                          </div>
                          <div v-else class="text-right text-gray-500">-</div>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot
                      v-if="yearlyComparisonData.length > 1"
                      class="font-medium bg-gray-100"
                    >
                      <tr>
                        <td class="px-4 py-2">Ringkasan</td>
                        <td class="px-4 py-2 text-right">
                          {{
                            yearlyComparisonData
                              .reduce((sum, item) => sum + item.ganjil, 0)
                              .toLocaleString()
                          }}
                        </td>
                        <td class="px-4 py-2 text-right">
                          {{
                            yearlyComparisonData
                              .reduce((sum, item) => sum + item.genap, 0)
                              .toLocaleString()
                          }}
                        </td>
                        <td class="px-4 py-2 text-right">
                          {{
                            yearlyComparisonData
                              .reduce((sum, item) => sum + item.total, 0)
                              .toLocaleString()
                          }}
                        </td>
                        <td class="px-4 py-2 text-right">
                          <div class="flex items-center justify-end">
                            <span
                              :class="
                                yearlyComparisonData[
                                  yearlyComparisonData.length - 1
                                ].total -
                                  yearlyComparisonData[0].total >
                                0
                                  ? 'text-green-600'
                                  : yearlyComparisonData[
                                      yearlyComparisonData.length - 1
                                    ].total -
                                      yearlyComparisonData[0].total <
                                    0
                                  ? 'text-red-600'
                                  : 'text-gray-600'
                              "
                              class="font-medium"
                            >
                              {{
                                yearlyComparisonData[
                                  yearlyComparisonData.length - 1
                                ].total -
                                  yearlyComparisonData[0].total >
                                0
                                  ? "+"
                                  : ""
                              }}{{
                                (
                                  yearlyComparisonData[
                                    yearlyComparisonData.length - 1
                                  ].total - yearlyComparisonData[0].total
                                ).toLocaleString()
                              }}
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                  <!-- Summary Stats -->
                  <div class="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-3">
                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-sm font-medium text-gray-700">
                        Tahun dengan Mahasiswa Terbanyak
                      </div>
                      <div class="flex items-center justify-between mt-1">
                        <span class="text-xl font-bold text-indigo-600">
                          {{
                            yearlyComparisonData.length > 0
                              ? yearlyComparisonData.reduce(
                                  (max, item) =>
                                    max.total > item.total ? max : item,
                                  { total: 0, tahun_akademik: "N/A" }
                                ).tahun_akademik
                              : "N/A"
                          }}
                        </span>
                        <span class="text-sm font-medium text-gray-700">
                          {{
                            yearlyComparisonData.length > 0
                              ? yearlyComparisonData
                                  .reduce(
                                    (max, item) =>
                                      max.total > item.total ? max : item,
                                    { total: 0 }
                                  )
                                  .total.toLocaleString()
                              : "0"
                          }}
                          mahasiswa
                        </span>
                      </div>
                    </div>

                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-sm font-medium text-gray-700">
                        Pertumbuhan Terbesar
                      </div>
                      <div class="flex items-center justify-between mt-1">
                        <span class="text-xl font-bold text-green-600">
                          {{
                            yearlyComparisonData.length > 1 &&
                            yearlyComparisonData
                              .slice(1)
                              .some((item) => item.growth > 0)
                              ? yearlyComparisonData
                                  .slice(1)
                                  .reduce(
                                    (max, item) =>
                                      item.growth > 0 &&
                                      (max.growth || 0) < item.growth
                                        ? item
                                        : max,
                                    { growth: 0, tahun_akademik: "N/A" }
                                  ).tahun_akademik
                              : "N/A"
                          }}
                        </span>
                        <span class="text-sm font-medium text-gray-700">
                          {{
                            yearlyComparisonData.length > 1 &&
                            yearlyComparisonData
                              .slice(1)
                              .some((item) => item.growth > 0)
                              ? "+" +
                                yearlyComparisonData
                                  .slice(1)
                                  .reduce(
                                    (max, item) =>
                                      item.growth > 0 &&
                                      (max.growth || 0) < item.growth
                                        ? item
                                        : max,
                                    { growth: 0 }
                                  )
                                  .growth?.toLocaleString()
                              : "-"
                          }}
                          mahasiswa
                        </span>
                      </div>
                    </div>

                    <div class="p-3 bg-white border border-gray-100 rounded-lg">
                      <div class="text-sm font-medium text-gray-700">
                        Rata-rata per Tahun
                      </div>
                      <div class="flex items-center justify-between mt-1">
                        <span class="text-xl font-bold text-amber-600">
                          {{
                            Math.round(
                              yearlyComparisonData.reduce(
                                (sum, item) => sum + item.total,
                                0
                              ) / yearlyComparisonData.length
                            ).toLocaleString()
                          }}
                        </span>
                        <span class="text-sm font-medium text-gray-700">
                          mahasiswa/tahun
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pesan jika tidak ada data sama sekali -->
              <div
                v-if="!hasData && chartData.value.length === 0"
                class="p-8 mt-4 text-center border border-gray-100 rounded-lg bg-gray-50"
              >
                <div class="flex flex-col items-center">
                  <svg
                    class="w-16 h-16 mb-3 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1"
                      d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1"
                      d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                    ></path>
                  </svg>
                  <h3 class="mb-1 text-base font-medium text-gray-600">
                    Tidak Ada Data Grafik
                  </h3>
                  <p class="max-w-md text-sm text-gray-500">
                    Saat ini belum ada data grafik mahasiswa yang tersedia untuk
                    ditampilkan.
                  </p>
                  <p class="mt-2 text-xs text-gray-400">
                    Silakan hubungi administrator untuk menambahkan data grafik
                    mahasiswa.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
